import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { Btn, ErrorNote, Field, Input } from "@/components/kmt/ui";
import { errMsg, publicApi } from "@/lib/api";
import { prettyPhone, todayCentral } from "@/lib/format";

export default function SignAgreement() {
  const { token } = useParams();
  const [doc, setDoc] = useState<any>(null);
  const [loadErr, setLoadErr] = useState("");
  const [f, setF] = useState({ full_name: "", phone: "", signature: "", agree: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicApi.get(`/public/agreements/${token}`).then((r) => {
      setDoc(r.data);
      setF((s) => ({ ...s, full_name: r.data.name, phone: prettyPhone(r.data.phone) }));
    }).catch((e) => setLoadErr(errMsg(e)));
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const device = `${navigator.userAgent} | screen ${window.screen.width}x${window.screen.height} | ${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
      await publicApi.post(`/public/agreements/${token}/sign`, { ...f, signed_date: todayCentral(), device });
      setDone(true);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink px-5 py-10 font-body text-mist md:px-10">
      <div className="mx-auto max-w-3xl">
        <Logo />
        <p className="eyebrow mt-10">Master Agreement</p>
        <h1 className="mt-3 font-display text-3xl text-gold sm:text-4xl">Review and sign</h1>
        <div className="gold-rule mt-6 w-32" />
        {loadErr && <div className="mt-8"><ErrorNote msg={loadErr} /></div>}
        {done && (
          <div className="mt-10 flex flex-col gap-4" data-testid="sign-success">
            <CheckCircle2 className="text-gold" size={40} />
            <p className="font-display text-2xl">Signed. You're all set.</p>
            <p className="text-[15px] text-mist/80">A copy is saved on file with KMT. You only need to sign once unless the terms change.</p>
            <p className="font-script text-3xl text-gold">Thank you for choosing KMT Private Rides!</p>
          </div>
        )}
        {doc && !done && (
          <>
            <p className="mt-6 text-[14px] text-mist/70" data-testid="sign-version">Version {doc.version}</p>
            <article className="mt-4 max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded-md border border-gold/25 bg-aubergine/70 p-6 text-[15px] leading-relaxed" data-testid="sign-agreement-text" tabIndex={0}>
              {doc.text}
            </article>
            <form onSubmit={submit} className="mt-10 grid gap-6" data-testid="sign-form">
              <div className="grid gap-6 md:grid-cols-2">
                <Field label="Full legal name" htmlFor="sg-name">
                  <Input id="sg-name" required minLength={2} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="sign-full-name" />
                </Field>
                <Field label="Mobile phone" htmlFor="sg-phone">
                  <Input id="sg-phone" required type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="sign-phone" />
                </Field>
              </div>
              <Field label="Type your full name as your signature" htmlFor="sg-sig">
                <Input id="sg-sig" required minLength={2} value={f.signature} onChange={(e) => setF({ ...f, signature: e.target.value })} data-testid="sign-signature" />
              </Field>
              {f.signature && <p className="font-script text-4xl text-gold" aria-hidden="true">{f.signature}</p>}
              <p className="text-[14px] text-mist/70">Date: {todayCentral()} (Central time). The exact time is stamped when you sign.</p>
              <label className="flex min-h-[48px] cursor-pointer items-start gap-3 text-[15px]">
                <input type="checkbox" className="mt-1 h-6 w-6 accent-[#65238E]" checked={f.agree} onChange={(e) => setF({ ...f, agree: e.target.checked })} data-testid="sign-agree-checkbox" />
                I have read the Master Agreement and agree to its terms. I understand my typed name is my signature.
              </label>
              <ErrorNote msg={err} />
              <Btn type="submit" disabled={busy || !f.agree} data-testid="sign-submit-btn" className="w-full md:w-auto">{busy ? "Signing…" : "Sign agreement"}</Btn>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
